
import Vue from 'vue';
import IllinoisTabBar from '../components/TabBar.vue';
import { SportLiveItem } from '../../../../types/sport';
import SportIcons from '../components/SportIcons.vue';
import OddsEventsGroup from '../components/OddsComponents/OddsEventsGroup.vue';
import { addOddToCartV3FromLive } from '@/composables/useOdds';
import type {
  LiveEventsCountResult,
  PreviewEventGroup,
  PreviewEvent,
  PreviewOutcome
} from '../../../../types/sport';

export default Vue.extend({
  name: 'IllinoisLive',
  components: { IllinoisTabBar, SportIcons, OddsEventsGroup },
  data() {
    return {
      activeSport: null as string | null,
      isLiveLoading: false as boolean
    };
  },
  computed: {
    skeletonGroup(): any {
      return { scheduled: '', events: [] } as any;
    },
    sportLive(): any[] {
      const available =
        this.$store.getters['IllinoisSport/getLiveSportsAvailable'] &&
        Array.isArray(
          this.$store.getters['IllinoisSport/getLiveSportsAvailable']
        )
          ? this.$store.getters['IllinoisSport/getLiveSportsAvailable']
          : [];
      return available;
    },
    activeSportItem(): SportLiveItem | null {
      const id = this.activeSport;
      if (!id) return null;
      return (
        (this.sportLive || []).find(s => String(s.id) === String(id)) || null
      );
    },
    tabItems(): { label: string; value: string; sportId?: string }[] {
      return (this.sportLive || [])
        .sort((a, b) => a.order - b.order)
        .map(s => ({
          label: s.name,
          value: String(s.id),
          sportId: String(s.id)
        }));
    },
    liveResult(): LiveEventsCountResult | null {
      if (!this.activeSport) return null;
      const getter = (this.$store.getters as any)[
        'IllinoisSport/getLiveEventsBySport'
      ];
      return typeof getter === 'function' ? getter(this.activeSport) : null;
    },
    liveGroupsAll(): PreviewEventGroup[] {
      const res = this.liveResult as any;
      if (!res || !Array.isArray(res.groupedEvents)) return [] as any;

      const mapOutcome = (o: any, marketId: string | number) => ({
        unique_id: o.unique_id,
        outcome_id: o.outcome_id,
        outcome_odd_value: o.outcome_odd_value || '',
        status: o.status,
        name: o.name,
        outcome_alias: o.outcome_alias || '',
        outcome_name: o.outcome_name || '',
        id_outcome: o.outcome_id || o.id_outcome || 0,
        ext_id: o.ext_id || '',
        lastUpdate: o.lastUpdate || '',
        sort: o.sort || 0,
        alias: o.alias || '',
        odd_percent: '',
        outcome_odd_value_for_order: o.outcome_odd_value_for_order || '',
        active: true,
        specifier: o.specifier || '',
        type: o.type || 'live',
        marketId: marketId,
        ...(o.Line != null && { Line: o.Line }),
        ...(o.BaseLine != null && { BaseLine: o.BaseLine })
      });

      const mapOdd = (odd: any) => {
        // Converti outcome da oggetto a array se necessario
        const outcomesArray = Array.isArray(odd.outcome)
          ? odd.outcome
          : Object.values(odd.outcome || {});

        // Calcola marketId: usa unique_id se presente, altrimenti id
        const marketId = odd.unique_id || odd.id;

        const outcomes = outcomesArray.map((o: any) => mapOutcome(o, marketId));

        // Ordina per sort se disponibile
        outcomes.sort((a: any, b: any) => (a.sort || 0) - (b.sort || 0));

        return {
          id: odd.id,
          unique_id: odd.unique_id,
          outcome: outcomes,
          sort: odd.sort || 0,
          specifier: odd.specifier || '',
          specifierSort: odd.specifierSort || 0,
          marketName: odd.marketName || '',
          multiplicity: odd.multiplicity || '',
          multiplicityMin: odd.multiplicityMin || 0,
          multiplicityMax: odd.multiplicityMax || 0,
          odd_with_specifier: odd.odd_with_specifier || false,
          ...(odd.mainLine != null && { mainLine: String(odd.mainLine) })
        };
      };

      const mapEvent = (e: any) => {
        const live = (this.$store.getters as any)[
          'IllinoisSport/getLiveEventById'
        ];
        const liveEntry =
          typeof live === 'function' ? live(String(e.id)) : null;
        const oddsObj =
          liveEntry && liveEntry.event ? liveEntry.event.odds : {};

        // Converti l'oggetto normalizzato in array
        const allOdds = Array.isArray(oddsObj)
          ? oddsObj
          : Object.values(oddsObj || {});

        // Mappa tutte le odds senza filtri
        const mappedOdds = allOdds.map(mapOdd);

        // Raggruppa le odds per id del market e unisci tutti gli outcome
        const oddsGroupedById: Record<string, any> = {};
        for (const odd of mappedOdds) {
          const marketId = String(odd.id);
          if (!oddsGroupedById[marketId]) {
            // Prima odd con questo id: crea la struttura base
            oddsGroupedById[marketId] = {
              id: odd.id,
              unique_id: odd.unique_id,
              outcome: [...odd.outcome],
              sort: odd.sort || 0,
              specifier: odd.specifier || '',
              specifierSort: odd.specifierSort || 0,
              marketName: odd.marketName || '',
              multiplicity: odd.multiplicity || '',
              multiplicityMin: odd.multiplicityMin || 0,
              multiplicityMax: odd.multiplicityMax || 0,
              odd_with_specifier: odd.odd_with_specifier || false,
              ...(odd.mainLine != null && { mainLine: String(odd.mainLine) })
            };
          } else {
            // Unisci gli outcome di questa odd con quelli già presenti
            const existingOutcomes = oddsGroupedById[marketId].outcome || [];
            const newOutcomes = odd.outcome || [];
            // Combina gli outcome, evitando duplicati per unique_id
            const outcomeMap = new Map();
            existingOutcomes.forEach((out: any) => {
              outcomeMap.set(out.unique_id, out);
            });
            newOutcomes.forEach((out: any) => {
              if (!outcomeMap.has(out.unique_id)) {
                outcomeMap.set(out.unique_id, out);
              }
            });
            oddsGroupedById[marketId].outcome = Array.from(outcomeMap.values());
            // Ordina gli outcome per sort
            oddsGroupedById[marketId].outcome.sort(
              (a: any, b: any) => (a.sort || 0) - (b.sort || 0)
            );
          }
        }

        // Converti in array di odds uniche (una per ogni id)
        const odds = Object.values(oddsGroupedById).map((odd: any) => {
          // Se odd_with_specifier === true e mainLine è presente, filtra per mainLine
          if (
            (odd.odd_with_specifier === true ||
              (odd.specifier && odd.specifier.toLowerCase() !== 'noSpec')) &&
            odd.mainLine != null
          ) {
            const mainLine = String(odd.mainLine);
            odd.outcome = odd.outcome.filter((outcome: any) => {
              const specifier = outcome.BaseLine;
              return (
                specifier === null ||
                specifier === undefined ||
                specifier === '' ||
                specifier === mainLine ||
                mainLine === null
              );
            });
          }
          return odd;
        });

        return {
          _id: e._id,
          scheduled: e.scheduled,
          quickbet: e.quickbet,
          count: e.count,
          id: e.id,
          type: e.type,
          livescore: e.livescore,
          odds,
          sport_event_status: e.sport_event_status,
          home_id: e.home_id,
          away_id: e.away_id,
          home: e.home,
          away: e.away,
          categoryId: e.categoryId,
          tournamentId: e.tournamentId,
          tournamentName: e.tournamentName
        };
      };

      return (res.groupedEvents as any[])
        .filter((g: any) => Array.isArray(g) && g.length > 0)
        .map((g: any[]) => ({
          scheduled: g[0].scheduled,
          events: g.map(mapEvent)
        })) as unknown as PreviewEventGroup[];
    },
    // columnsForLive(): string[] {
    //   const groups = this.liveGroupsFiltered || [];
    //   if (groups.length === 0) {
    //     return [];
    //   }

    //   // Raccogli tutti gli eventi da tutti i gruppi
    //   const allEvents: PreviewEvent[] = [];
    //   groups.forEach(group => {
    //     if (Array.isArray(group.events)) {
    //       allEvents.push(...group.events);
    //     }
    //   });

    //   if (allEvents.length === 0) {
    //     return [];
    //   }

    //   // Determina il tipo di struttura guardando il primo evento con odds valide
    //   let structureType: 'multiple-odds' | 'single-odd' | null = null;
    //   let firstEventWithOdds: PreviewEvent | null = null;

    //   for (const event of allEvents) {
    //     if (event.odds && Array.isArray(event.odds) && event.odds.length > 0) {
    //       firstEventWithOdds = event;
    //       structureType =
    //         event.odds.length > 1 ? 'multiple-odds' : 'single-odd';
    //       break;
    //     }
    //   }

    //   if (!firstEventWithOdds || !structureType) {
    //     return [];
    //   }

    //   // Se odds.length > 1: i nomi delle colonne sono i marketName di ogni odd
    //   if (structureType === 'multiple-odds') {
    //     const columnsSet = new Set<string>();
    //     const columnsOrder: string[] = [];

    //     // Prima passata: raccogli tutte le colonne mantenendo l'ordine del primo evento
    //     if (firstEventWithOdds.odds) {
    //       firstEventWithOdds.odds.forEach(odd => {
    //         const marketName = odd.marketName || '';
    //         if (marketName && !columnsSet.has(marketName)) {
    //           columnsSet.add(marketName);
    //           columnsOrder.push(marketName);
    //         }
    //       });
    //     }

    //     // Seconda passata: aggiungi colonne da altri eventi che non sono già presenti
    //     allEvents.forEach(event => {
    //       if (event.odds && Array.isArray(event.odds)) {
    //         event.odds.forEach(odd => {
    //           const marketName = odd.marketName || '';
    //           if (marketName && !columnsSet.has(marketName)) {
    //             columnsSet.add(marketName);
    //             columnsOrder.push(marketName);
    //           }
    //         });
    //       }
    //     });

    //     return columnsOrder;
    //   }

    //   // Se odds.length === 1: i nomi delle colonne sono i nomi degli outcomes
    //   const firstOdd = firstEventWithOdds.odds[0];
    //   if (!firstOdd || !firstOdd.outcome || !Array.isArray(firstOdd.outcome)) {
    //     return [];
    //   }

    //   const columnsSet = new Set<string>();
    //   const columnsOrder: string[] = [];

    //   // Prima passata: raccogli tutte le colonne mantenendo l'ordine del primo evento
    //   const firstOutcomes = firstOdd.outcome as PreviewOutcome[];
    //   firstOutcomes.forEach(outcome => {
    //     const columnName = outcome.outcome_name || outcome.outcome_alias || '';
    //     if (columnName && !columnsSet.has(columnName)) {
    //       columnsSet.add(columnName);
    //       columnsOrder.push(columnName);
    //     }
    //   });

    //   // Seconda passata: aggiungi colonne da altri eventi che non sono già presenti
    //   allEvents.forEach(event => {
    //     if (
    //       event.odds &&
    //       Array.isArray(event.odds) &&
    //       event.odds.length === 1 &&
    //       event.odds[0].outcome &&
    //       Array.isArray(event.odds[0].outcome)
    //     ) {
    //       const outcomes = event.odds[0].outcome as PreviewOutcome[];
    //       outcomes.forEach(outcome => {
    //         const columnName =
    //           outcome.outcome_name || outcome.outcome_alias || '';
    //         if (columnName && !columnsSet.has(columnName)) {
    //           columnsSet.add(columnName);
    //           columnsOrder.push(columnName);
    //         }
    //       });
    //     }
    //   });

    //   return columnsOrder;
    // },
    columnsForLive(): string[] {
      const res = this.liveResult as any;
      if (!res || !res.headerSign || !Array.isArray(res.headerSign)) {
        return [];
      }

      const headerSign = res.headerSign;

      // Se headerSign.length === 1: le colonne sono i name degli outcomes di quel singolo headerSign
      if (headerSign.length === 1) {
        const outcomes = headerSign[0].outcomes || [];
        return outcomes
          .map((outcome: any) => outcome.name || '')
          .filter(Boolean);
      }

      // Se headerSign.length > 1: le colonne sono i name di ogni headerSign nell'array (ordinati per sort)
      const sortedHeaderSign = [...headerSign].sort(
        (a: any, b: any) => (a.sort || 0) - (b.sort || 0)
      );
      return sortedHeaderSign.map((h: any) => h.name || '').filter(Boolean);
    },
    liveGroupsFiltered(): PreviewEventGroup[] {
      const groups = this.liveGroupsAll || [];
      if (groups.length === 0) {
        return [];
      }

      return groups
        .map(group => {
          const filteredEvents = (group.events || []).filter(event => {
            const sportEventStatus =
              event.sport_event_status?._attributes?.status;
            const scoreboardStatus = event.livescore?.Scoreboard?.Status;

            // Mantieni l'evento se almeno uno dei due status è diverso da 2
            return sportEventStatus === 2 && scoreboardStatus === 2;
          });

          // Restituisci il gruppo solo se ha almeno un evento dopo il filtro
          if (filteredEvents.length === 0) {
            return null;
          }

          return {
            ...group,
            events: filteredEvents
          };
        })
        .filter((group): group is PreviewEventGroup => group !== null);
    }
  },
  methods: {
    addOddToCartV3FromLive,
    t(key: string, values?: Record<string, unknown> | any[]) {
      return this.$t(key, values) as string;
    },
    onTabChange(value: string) {
      this.activeSport = value;
      // avvia il loading immediatamente quando si cambia sport
      this.isLiveLoading = true;
      // non bloccare: avvia fetch senza attendere, lascia skeleton visibili
      this.$nextTick(this.ensureLiveData);
    },
    getCategoryId(group: any): string {
      const ev = group && group.events && group.events[0];
      return ev && ev.categoryId != null ? String(ev.categoryId) : '0';
    },
    getTournamentId(group: any): string {
      const ev = group && group.events && group.events[0];
      return ev && ev.tournamentId != null ? String(ev.tournamentId) : '0';
    },
    getTournamentName(group: any): string {
      const ev = group && group.events && group.events[0];
      return ev && ev.tournamentName ? String(ev.tournamentName) : '';
    },
    getCategoryName(group: any): string {
      const ev = group && group.events && group.events[0];
      return ev && ev.categoryName ? String(ev.categoryName) : '';
    },
    ensureLiveData() {
      if (!this.activeSport) {
        this.isLiveLoading = false;
        return;
      }
      const item: any = this.activeSportItem as any;
      if (item) {
        // lancia il fetch senza bloccare la UI principale
        this.$store
          .dispatch('IllinoisSport/fetchLiveEventsCountBySportOverrideOdds', {
            sportId: Number(item.id),
            sport_id: String(item._id)
          })
          .finally(() => {
            this.isLiveLoading = false;
          });
      } else {
        // se non c'è l'item, ferma il loading
        this.isLiveLoading = false;
      }
    }
  },
  async mounted() {
    window.parent.postMessage(
      {
        type: 'livePageOpened'
      },
      '*'
    );
    // carica lista sport live e conteggi live
    this.$store.dispatch('IllinoisSport/setFieldStatisticsEventId', {
      eventId: null
    });
    this.$store.dispatch('IllinoisSport/fetchLiveSports');
    this.$store.dispatch('IllinoisSport/setFieldStatisticsSportId', {
      sportId: this.activeSport
    });
    this.isLiveLoading = true;
    // avvia in background; il titolo/tab mostrano già skeleton
    this.$store
      .dispatch(
        'IllinoisSport/fetchLiveEventsCountBySportOverrideOdds',
        {} as any
      )
      .finally(() => {
        this.isLiveLoading = false;
      });
    if (!this.activeSport && this.sportLive && this.sportLive.length) {
      this.activeSport = String((this.sportLive[0] as any).id);
      this.ensureLiveData();
    }
  },
  watch: {
    sportLive(newList: any[]) {
      if (!this.activeSport && Array.isArray(newList) && newList.length) {
        this.activeSport = String((newList[0] as any).id);
        this.ensureLiveData();
      }
    },
    activeSport(newVal) {
      this.$store.dispatch('IllinoisSport/setFieldStatisticsSportId', {
        sportId: newVal
      });
      this.$store.dispatch('IllinoisSport/setActiveLiveSport', {
        sportId: newVal
      });
    }
  }
});
